import { Alert, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { ExpandableConfig } from 'antd/es/table/interface'
import { Breakpoint } from 'antd/lib/_util/responsiveObserver'
import React, { FunctionComponent, useCallback, useMemo } from 'react'

import { useAppSelector } from '../../../redux/hooks'
import {
  dualStringSorter,
  numberSorter,
  stringSorter,
  utcDateSorter,
} from '../../utils/generalTableHelpers'
import { sparsePagination } from '../../utils/layoutConst'
import { useColumnFilters } from '../../utils/useColumnFilters'
import { useTableLocale } from '../../utils/useTableLocale'
import {
  useZustavateData,
  ZustavateTableItem,
} from '../../utils/useZustavateData'
import { AddressLink } from '../AddressLink/AddressLink'
import { Min } from '../DateFormat/DateFormat'
import { ExpandColumnTitle } from '../ExpandColumnTitle/ExpandColumnTitle'
import { ExpandIcon } from '../ExpandIcon/ExpandIcon'
import { Money } from '../Money/Money'
import { ZustavateDataTableAction } from '../ZustavateDataTableAction/ZustavateDataTableAction'
import { ZustavateDataTableExpanded } from '../ZustavateDataTableExpanded/ZustavateDataTableExpanded'

const tableId = 'zustavate-data-table'
const scrollX = { x: 1400 }

const priceRenderer = (amount?: number | null) => (
  <Money
    symbol="Kč"
    grayscale
    decimalNumbers={0}
    amount={amount ? amount : undefined}
    noData="---"
  />
)

export const ZustavateDataTable: FunctionComponent = () => {
  const { data, loading, error, refetch } = useZustavateData()
  const expandedRowKeys = useAppSelector(
    state => state.expandedRow.tables?.[tableId] || []
  )

  const {
    searchFilter,
    dateFilter,
    propertyTypeFilter,
    dualSearchFilter,
    moneyFilter,
  } = useColumnFilters<ZustavateTableItem>()

  const rowClassName = useCallback(
    (record: ZustavateTableItem) => {
      const isExpanded = expandedRowKeys.includes(record.id)
        ? 'rowExpanded fade-in'
        : ''
      return isExpanded
    },
    [expandedRowKeys]
  )

  const columns = useMemo<ColumnsType<ZustavateTableItem>>(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        responsive: ['' as Breakpoint],
      },
      {
        title: 'Vytvořeno',
        dataIndex: 'createdAt',
        width: 130,
        sorter: utcDateSorter('createdAt'),
        ...dateFilter('createdAt'),
        render: (createdAt: string) => <Min>{createdAt}</Min>,
      },
      {
        title: 'Typ',
        dataIndex: 'propertyTypeLabel',
        className: 'first-letter:uppercase',
        width: 90,
        ...propertyTypeFilter('propertyTypeLabel'),
        sorter: stringSorter('propertyTypeLabel'),
      },
      {
        title: 'E-mail',
        dataIndex: 'email',
        className: 'whitespace-nowrap select-all',
        ellipsis: true,
        width: 130,
        sorter: stringSorter('email'),
        ...searchFilter('email', 'Hledat email'),
      },
      {
        title: 'Telefon',
        dataIndex: 'phone',
        width: 120,
        sorter: stringSorter('phone'),
        ...searchFilter('phone', 'Hledat telefon'),
      },
      {
        title: 'Ulice',
        dataIndex: 'street',
        ellipsis: true,
        width: 170,
        sorter: stringSorter('street'),
        ...searchFilter('street', 'Hledat ulici'),
        render: (street, record) => (
          <AddressLink
            noOfficeIndicator
            addressId={record.addressCode}
            drawerTarget
          >
            {street}
          </AddressLink>
        ),
      },
      {
        title: 'Město',
        dataIndex: 'city',
        ellipsis: true,
        width: 150,
        sorter: stringSorter('city'),
        ...searchFilter('city', 'Hledat město'),
      },
      {
        title: 'Cena od',
        dataIndex: 'minPrice',
        width: 120,
        className: '!text-right',
        sorter: numberSorter('minPrice'),
        ...moneyFilter('minPrice'),
        render: priceRenderer,
      },
      {
        title: 'Cena do',
        dataIndex: 'maxPrice',
        width: 120,
        className: '!text-right',
        sorter: numberSorter('maxPrice'),
        ...moneyFilter('maxPrice'),
        render: priceRenderer,
      },
      {
        title: 'Poznámka',
        dataIndex: 'note',
        sorter: dualStringSorter('note', 'lastMessage'),
        ...dualSearchFilter('note', 'lastMessage', 'Hledat v poznámce'),
        render: (_, record) => (
          <ZustavateDataTableAction record={record} onUpdate={refetch} />
        ),
      },
    ],
    [
      dateFilter,
      dualSearchFilter,
      moneyFilter,
      propertyTypeFilter,
      refetch,
      searchFilter,
    ]
  )

  const expandable = useMemo<ExpandableConfig<ZustavateTableItem>>(() => {
    const allKeys = (data || []).map(record => record.id)

    return {
      expandedRowKeys,
      expandedRowRender: record => (
        <ZustavateDataTableExpanded record={record} />
      ),
      columnTitle: <ExpandColumnTitle allKeys={allKeys} tableId={tableId} />,
      expandIcon: ({ expanded, record }) => (
        <ExpandIcon
          expanded={expanded}
          recordId={record.id}
          tableId={tableId}
          expandable
        />
      ),
      rowExpandable: () => true,
    }
  }, [data, expandedRowKeys])

  const tableLocale = useTableLocale(loading)

  if (error) {
    return (
      <Alert
        message="Chyba při načítání dat ze Zustavate databáze"
        type="error"
      />
    )
  }

  return (
    <Table
      id={tableId}
      rowKey="id"
      rowClassName={rowClassName}
      size="small"
      scroll={scrollX}
      dataSource={data}
      columns={columns}
      loading={loading}
      locale={tableLocale}
      pagination={sparsePagination}
      expandable={expandable}
    />
  )
}
